/* eslint consistent-return: 0, class-methods-use-this: 0 */
import { ModuleConsts, UserSearchValueMap, UsersConsts } from 'client/util/Constants';
import { get, map } from 'lodash';
import {
  getSearchField,
  setFilterTerm,
  setSearchTerm,
} from 'client/api/v2/apiUtils';
import Api from 'api/v2/Api';
import CollectionApi from 'api/v2/CollectionApi';
import DefaultSystemUserModel from 'client/store/modules/models/DefaultSystemUserModel';
import Util from 'client/util/Util';
import axios from 'axios';

const { suspended } = ModuleConsts.systemUser.accountStates;

const getDefaultParams = () => ({
  skip: 0,
  limit: 50,
  sort: 'lastname',
  search: {
    fields: [
      'email',
      'firstname',
      'lastname',
      'username',
      'displayname',
      'department',
      'employeeIdentifier',
      'employeeType',
      'jobTitle',
      'location',
      'systemUsername',
    ],
    searchTerm: '',
  },
});
class SystemUserApi extends CollectionApi {
  constructor(baseUrl = '/api/systemusers', defaultParams = getDefaultParams()) {
    super(baseUrl, defaultParams, 100);
  }

  /**
   * Override the create method to NOT send
   * activation email upon creation (activation
   * email will be sent from a later confirmation
   * modal) see TN-155 for details
   * @param {object} user
   * @param {boolean} shouldSendEmail
   * @returns {object}
   */
  create(user, shouldSendEmail = true) {
    return new Promise((resolve, reject) => {
      const mesg = 'Unable to create item!';
      this.initialize()
        .then(() => {
          axios
            .post(`${this.baseUrl}?fullValidationDetails=password`, {
              ...user,
              creationMetadata: { shouldSendActivationEmail: shouldSendEmail },
            })
            .then(resp => resolve(resp.data))
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  /**
   * Override method from base class to include query param that
   * allows Manager to be cleared from users affected by Manager deletion
   * Eventually will also allow to pass an userId to populate affected
   * users with a new Manager
   * @param  {String} id [The id of the object that will be deleted]
   * @return {Object}    [A promise]
   */
  delete(id) {
    return new Promise((resolve, reject) => {
      const mesg = 'Unable to delete item!';
      // Eventually this will be populated by a new param
      const manager = 'null';
      const params = {
        cascade_manager: manager,
      };
      this.initialize()
        .then(() => {
          axios
            .delete(`${this.baseUrl}/${id}`, { params })
            .then(resp => resolve(resp.data))
            .catch((error) => {
              Api.handleError(reject, mesg, error);
            });
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  hasCustomFilter(obj) {
    // Custom filters are only available by themselves with no additional filters applied
    if (Array.isArray(obj) && obj.length === 1) {
      return obj.every((item) => (typeof item) !== 'string');
    }
    return false;
  }

  customQueryString(obj) {
    let key = '';
    const {
      value,
      customType,
      name,
      operator,
    } = obj;

    value.split('|').forEach((val, index) => {
      key += `${index !== 0 ? '&' : ''}filter[${customType}]=${name}:${operator}:${val}`;
    });

    return key;
  }

  /**
   * Stringify an object of query string parameters for use in a GET request.
   * @see
   * https://stackoverflow.com/questions/1714786/query-string-encoding-of-a-javascript-object
   * http://api.jquery.com/jquery.param/
   */
  param(obj, prefix) {
    const str = [];

    Object.keys(obj).forEach((property) => {
      let key;
      /**
         * Custom Query string for systems v1 endpoint.
         * as of 1/2022 this is the only endpoint we are doing this for
         * currently this only allows the option of one custom filter
         * with no additional filters applied
         */

      if (property === 'filter' && this.hasCustomFilter(obj.filter)) {
        str.push(this.customQueryString(obj.filter[0]));
        return;
      }

      if (prefix === 'filter' && typeof obj[property] === 'object' && obj[property].logicalOperator) {
        key = `${prefix}[${obj[property].logicalOperator}][${property}]`;
      } else {
        key = prefix ? `${prefix}[${property}]` : property;
      }

      const value = get(obj[property], 'filterValue', obj[property]);
      if (value !== null && typeof value === 'object') {
        // Recurse to process nested objects
        str.push(this.param(value, key));
      } else {
        const typeOfValue = typeof value;
        // Add the key/value pair to the URL if the value is a boolean, number or non-empty string
        if (typeOfValue === 'boolean'
                    || typeOfValue === 'number'
                    || (typeOfValue === 'string' && value.trim().length !== 0)) {
          str.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
      }
    });

    return str.join('&');
  }

  /**
   * Override method from base class to use legacy search endpoint to searchfor system users. The
   * specified search terms will be split on white space, and passed as or criteria, so that the
   * admin can search on multiple terms in all searchable fields.
   *
   * In other words, given a search term of "Ally Pinkerton", the UI will make an API request to
   * search for system users with a firstname of "Ally", or a lastname of "Ally", or a firstname of
   * "Pinkerton", or a lastname of "Pinkerton".
   */
  async fetch(skip, limit, sort, searchTerm, cancelToken, filters = [], pageToken, skipToken) {
    // Only use the legacy search endpoint for free form searches (i.e. not for filters and not for
    // context searches - First name: Ally).

    if (searchTerm
        && filters.length === 0
        && this.getSearchField(searchTerm).field === undefined) {
      const params = {
        filter: {
          or: this.getOrSearchCriteria(searchTerm),
        },
      };
      const response = await axios.post('/api/search/systemusers', params);

      return response.data;
    }
    return super.fetch(skip, limit, sort, searchTerm, cancelToken, filters, pageToken, skipToken);
  }

  async fetchAll(searchTerm, boundItems, filters = []) {
    // Only use the legacy search endpoint for free form searches (i.e. not for filters and not for
    // context searches - First name: Ally).
    const fetchLimit = 100;
    if (searchTerm
        && filters.length === 0
        && this.getSearchField(searchTerm).field === undefined) {
      const queryAllUsers = (page = 0) => this.axios.post(
        '/api/search/systemusers',
        {
          filter: {
            or: this.getOrSearchCriteria(searchTerm),
          },
          skip: page * fetchLimit,
          limit: fetchLimit,
          sort: null,
        },
      );

      const firstRequest = queryAllUsers();
      let users = [];
      return firstRequest.then((response) => {
        if (response && response.data) {
          let { totalCount } = response.data;
          users = response.data.results;
          if (totalCount > fetchLimit) {
            const requests = [firstRequest];
            for (; totalCount > fetchLimit; totalCount -= fetchLimit) {
              requests.push(queryAllUsers(requests.length));
            }
            return Promise.all(requests).then(responses => responses
              .map(resp => resp.data.results)
              .reduce(((all, results) => all.concat(results)), users));
          }
        }

        return users;
      });
    }

    return super.fetchAll(searchTerm, boundItems, filters);
  }

  get(id, cancelToken) {
    const params = {
      fields: Object.keys(DefaultSystemUserModel()).join(' '),
    };

    return this
      .initialize()
      .then(() => axios.get(`${this.baseUrl}/${id}`, { cancelToken, params }))
      .then(resp => resp.data);
  }

  getBoundApplications(id, params) {
    return axios.get(`${UsersConsts.APIPaths.users}${id}/applications`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getBoundDevices(id, params) {
    return axios.get(`${UsersConsts.APIPaths.users}${id}/systems`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getBoundDirectories(id, params) {
    return axios.get(`${UsersConsts.APIPaths.users}${id}/directories`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getBoundUserGroups(id, params) {
    return axios.get(`${UsersConsts.APIPaths.users}${id}/memberof`, { params })
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  /**
   * Get the devices that are associated (directly bound) to a system user.
   * @param { String} id - System User id
   * @returns Returns an array of objects that contains ids for the devices.
   * In order to get the name, another query will be required.
   * @see SystemUserModel.js -> fetchDeviceAssociations()
   *
   * Note: The max limit on this api call is 100. If we need to get more
   * devices in the future, this will need to be refactored.
   */
  getDeviceAssociations(id) {
    return axios.get(`/api/v2/users/${id}/associations?limit=100&targets=system`)
      .then(resp => ({
        results: resp.data,
        totalCount: +resp.headers['x-total-count'],
      }));
  }

  getSearchField(searchTerm) {
    return getSearchField(searchTerm, UserSearchValueMap);
  }

  /**
   * @description Format search string of words into array
   * of search criteria for all default user fields in order to
   * search every field for every term entereded into search
   * populate params.filter.or.value
   */
  getOrSearchCriteria(searchTerm) {
    // Remove extranous white space and return an array of words to search on. e.g.:
    // ' Term1   Term2 ' is mapped to ['Term1', 'Term2']

    const fixedSearchTerm = Util.getSpecialCharacterEscapedString(searchTerm);
    const words = fixedSearchTerm.split(' ').filter(word => !!word);

    // Create the array of or criteria to filter on, one for each field and word in the search
    // term. e.g.:
    //
    // "email:$regex:/Term1/i",
    // "email:$regex:/Term2/i",
    // "firstname:$regex:/Term1/i",
    // "firstname:$regex:Term2/i",

    const criteria = this.defaultParams.search.fields
      .flatMap(field => words.map(word => `${field}:$regex:/${word}/i`));

    return criteria;
  }

  /**
   * @description Make the ajax call to send activation emails to
   * the list of selected users.
   */
  sendEmail(selectedIDs, recipientEmail) {
    const payload = {
      isSelectAll: false,
      models: map(selectedIDs, id => ({ _id: id })),
    };

    if (recipientEmail) {
      payload.recipientEmail = recipientEmail;
    }

    return new Promise((resolve, reject) => {
      const mesg = 'An error occurred while attempting to send emails!';
      axios
        .post(
          '/api/systemusers/reactivate',
          payload,
        )
        .then((resp) => {
          resolve((resp && resp.data) || '');
        })
        .catch((error) => {
          Api.handleError(reject, mesg, error);
        });
    });
  }

  /**
   * @description Resends a password reset email or an
   * activation email.
   * Optional activationEmail is to send
   * activation email to an alt email
   */
  async sendSingleEmail(id, activationEmail) {
    const params = {
      activation_email: activationEmail,
    };
    try {
      await this.initialize();
      const res = await axios.get(`/systemusers/reactivate/${id}`, { params });
      return res.data;
    } catch {
      throw new Error('An error occurred while attempting to send email!');
    }
  }

  setSearchTerm(defaultParams, searchTerm) {
    return setSearchTerm(defaultParams, searchTerm, UserSearchValueMap);
  }

  /**
   * @param {String} id - The unique identifier of the user
   */
  expire(id) {
    return this.initialize()
      .then(() => axios.post(`${this.baseUrl}/${id}/expire`))
      .catch(() => {
        throw new Error('An error occurred when attempting to expire this user.');
      });
  }

  /**
   * @param {String} id - The unique identifier of the user
   * @param {Number} exclusionUntil - The MFA exclusion expiration date
   * @param {Number} exclusionDays - The number of days for MFA exclusion after user is
   *   moved from staged to activated
   */
  resetMFA(id, exclusionUntil, exclusionDays) {
    const mesg = 'An error occurred when attempting to reset the user\'s MFA.';
    let payload = { exclusion: true, exclusionUntil };
    if (exclusionDays) {
      payload = { exclusion: true, exclusionDays };
    }

    return this.initialize()
      .then(() => axios.post(`${this.baseUrl}/${id}/resetmfa`, payload))
      .then((resp) => {
        if (resp && resp.data) {
          return resp.data;
        }
        return Error(mesg);
      })
      .catch((error) => {
        if (error) {
          throw new Error(error);
        }
      });
  }

  /**
   * Filter system users
   * @see https://docs.jumpcloud.com/1.0/search/search-system-users
   *
   * @param {Object} filter - One and/or clause with an array of query
   * expressions
   */
  search(filter, fields, sort, skip = 0, limit = 50) {
    const body = {
      fields,
      filter,
      limit,
      skip,
      sort,
    };

    return this.initialize()
      .then(() => axios.post('/api/search/systemusers', body))
      .then((resp) => {
        if (resp && resp.data) return resp.data;
        return Promise.reject(new Error('Unable to search users'));
      });
  }

  /**
   * @param {String} id - The unique identifier of the user
   * @param {Boolean} state - The user's state
   */
  suspend(id, state) {
    const payload = {
      id,
      state,
      suspended: state === suspended, // May be deprecated in the future
    };
    return super.update(payload);
  }

  /**
   * @param {String} id - The unique identifier of the user
   */
  unlock(id) {
    return this.initialize()
      .then(() => axios.post(`${this.baseUrl}/${id}/unlock`))
      .then((resp) => {
        if (resp && resp.data) return resp.data;
        return Error('An error occurred when attempting to unlock this user.');
      })
      .catch((error) => {
        if (error) throw new Error(error);
      });
  }

  /**
   * @param  {object} user
   * @returns {object}
   */
  async update(user) {
    const response = await super.update(user);
    return response;
  }

  /**
   * @param {array} userIds - An Array of User Ids
   */
  async managers(userIds) {
    const results = await Promise.all(userIds.map(id => axios.get(`${this.baseUrl}?filter[0]=manager:eq:${id}`)));
    return userIds.filter((_, index) => results[index].data.totalCount > 0);
  }

  /**
   * State->Activate
   * (The logic for this endpoint is currently incomplete,
   * it only sends the Activated Welcome Email for now)
   * @see https://jumpcloud.atlassian.net/browse/TN-259
   * @see https://jumpcloud.atlassian.net/browse/DE-1910
   * @param {String} id - UserId
   * @param {Boolean} email - Determines whether or not the Activated Welcome Email is sent
   * sending empty body would SEND the welcome email.
   * in the future, 'email' can be an email address
   */
  async activate(id, email) {
    const body = {
      email,
    };

    return this.initialize()
      .then(() => axios.post(`/api/systemusers/${id}/state/activate`, body))
      .then((resp) => {
        if (resp && resp.data) return resp.data;
        // This error handling is provisional while the endpoint only sends the email
        return Promise.reject(new Error('Unable to send Welcome Email'));
      });
  }

  /**
   * Changes a system user's password
   * @param {string} id User Id
   * @param {string} password New password
   * @returns {Promise<true | string[]>} True if successful, or fields that failed validation
   */
  async changePassword(id, password) {
    await this.initialize();

    try {
      await axios.put(`/api/systemusers/${id}?fullValidationDetails=password`, { password });
      return true;
    } catch (e) {
      if (e.response.status === 400) {
        return e
          .response
          .data
          .details
          .flatMap(detail => detail.fieldViolationsList)
          .map(violation => violation.description);
      }

      throw e;
    }
  }
}

const api = new SystemUserApi();
api.setFilterTerm = setFilterTerm;
export default api;
export const SystemUserApiClass = SystemUserApi;
